import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, allVideos } from '@/mocks/videos';
import { siteConfig } from '@/mocks/site';

export default function Home() {
  const featuredVideos = allVideos.filter((v) => v.featured).slice(0, 8);
  const newVideos = allVideos.filter((v) => v.isNew).slice(0, 8);
  const topRated = [...allVideos].sort((a, b) => b.rating - a.rating).slice(0, 8);

  const getCategoryName = (slug: string) => {
    const cat = categories.find((c) => c.slug === slug);
    return cat ? cat.name : '';
  };

  const orgSchema = {
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
  };

  const webSiteSchema = {
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteConfig.url}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const webPageSchema = {
    name: `${siteConfig.name} - ${siteConfig.tagline}`,
    description: siteConfig.description,
    url: siteConfig.url,
  };

  return (
    <>
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="WebSite" data={webSiteSchema} />
      <SchemaOrg type="WebPage" data={webPageSchema} />

      <Navbar />

      <main>
        <section className="relative h-[520px] md:h-[650px] flex items-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20movie%20theater%20atmosphere%20with%20warm%20golden%20spotlight%20beams%2C%20abstract%20film%20reel%20texture%2C%20elegant%20theater%20curtains%20in%20deep%20red%2C%20dramatic%20lighting%2C%20professional%20cinematography%20aesthetic%2C%20rich%20warm%20tones%2C%20dark%20luxurious%20background&width=1920&height=1080&seq=hero-bg-home-01&orientation=landscape"
            alt="农民影视 - 高清电影电视剧在线观看"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                农民影视
              </h1>
              <p className="text-white/80 text-base md:text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed mb-6">
                打造影视大全，提供<strong className="text-accent-300">电影大全</strong>、<strong className="text-accent-300">电视剧全集</strong>、影视分类等所有影视资源，片库丰富，免费高清在线观看不缓冲
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/dongzuo"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  立即观影
                </Link>
                <Link
                  to="/category/dianshiju"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm text-white font-medium rounded-full hover:bg-white/20 transition-colors border border-white/20 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-tv-line text-lg"></i>
                  热门剧集
                </Link>
              </div>
              <div className="flex items-center justify-center gap-6 mt-8">
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-accent-400">10万+</div>
                  <div className="text-white/60 text-xs mt-1">影视资源</div>
                </div>
                <div className="w-px h-10 bg-white/20"></div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-accent-400">500万+</div>
                  <div className="text-white/60 text-xs mt-1">月活用户</div>
                </div>
                <div className="w-px h-10 bg-white/20"></div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-accent-400">4K</div>
                  <div className="text-white/60 text-xs mt-1">超高清画质</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">影视分类</h2>
              <p className="text-foreground-500 text-sm">海量片库，按类型快速找到你想看的内容</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="group flex flex-col items-center gap-3 p-5 md:p-6 bg-background-100 hover:bg-primary-50 rounded-xl transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <span className="text-sm md:text-base font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">精选推荐</h2>
                <p className="text-foreground-500 text-sm">编辑团队为您精心挑选的高分佳作</p>
              </div>
              <Link
                to="/category/dongzuo"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {featuredVideos.map((video) => (
                <MovieCard
                  key={video.id}
                  id={video.id}
                  title={video.title}
                  category={video.category}
                  year={video.year}
                  rating={video.rating}
                  type={video.type}
                  posterUrl={video.posterUrl}
                  isNew={video.isNew}
                  categoryName={getCategoryName(video.category)}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">最新上线</h2>
                <p className="text-foreground-500 text-sm">热映新片第一时间上线，追剧不等待</p>
              </div>
              <Link
                to="/category/kehuan"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {newVideos.map((video) => (
                <MovieCard
                  key={video.id}
                  id={video.id}
                  title={video.title}
                  category={video.category}
                  year={video.year}
                  rating={video.rating}
                  type={video.type}
                  posterUrl={video.posterUrl}
                  isNew={video.isNew}
                  categoryName={getCategoryName(video.category)}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">高分必看</h2>
                <p className="text-foreground-500 text-sm">口碑爆棚，每一部都值得花时间细细品味</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {topRated.map((video) => (
                <MovieCard
                  key={video.id}
                  id={video.id}
                  title={video.title}
                  category={video.category}
                  year={video.year}
                  rating={video.rating}
                  type={video.type}
                  posterUrl={video.posterUrl}
                  categoryName={getCategoryName(video.category)}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-primary-600">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">开始你的观影之旅</h2>
            <p className="text-white/80 text-sm md:text-base mb-8">海量高清影视资源，完全免费，无需注册，随时随地畅享观影体验</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/dongzuo"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 font-semibold rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-movie-line"></i>
                浏览电影
              </Link>
              <Link
                to="/about"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm text-white font-medium rounded-full hover:bg-white/20 transition-colors border border-white/30 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-information-line"></i>
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}